import { useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import chatApi from "../../../../api/chatApi";
import {
  useBrightbotActions,
  useBrightbotStore,
} from "../../../../store/useBrightbotStore";

import {
  useSessionActions,
  useSessionStore,
} from "../../../../store/useSessionStore";

type Props = {
  manualSubmit: (messageValue: string) => Promise<void>;
};

const useChatbotLifecycle = ({ manualSubmit }: Props) => {
  const { workspaceId, threadId } = useParams();
  const thread = useSessionStore.use.thread();

  const {
    setStream,
    fetchNewThread,
    updateOutput,
    setMessages,
    setLoading,
    setStreamMessage,
    resetState,
    setSidebar,
    setThread,
    setInterruptAction,
    clearSteps,
  } = useSessionActions();

  const activeAssistant = useBrightbotStore.use.activeAssistant();

  const { setActiveAssistant, setAssistants } = useBrightbotActions();

  const { pathname, state: locationState } = useLocation();

  const externalState = locationState as { brightbotText?: string };

  useEffect(() => {
    return () => {
      resetState();
    };
  }, []);

  // fetch all the assistants and set the superduper agent as the default active
  useEffect(() => {
    chatApi
      .post("/assistants/search", { limit: 1000, offset: 0 })
      .then((res) => {
        // make the superduper agent the default active agent
        setActiveAssistant(
          res.data.find((data: any) => data?.graph_id === "superduper_agent")
        );
        setAssistants(res.data);
      })
      .catch((err) => console.error(err));
  }, [setActiveAssistant, setAssistants]);

  useEffect(() => {
    if (pathname === `/workspace/${workspaceId}/brightagent`) {
      setThread(null);
      setMessages([]);
    }
  }, [pathname, workspaceId, setThread, setMessages]);

  // add data from external widget prompts
  useEffect(() => {
    if (externalState?.brightbotText && activeAssistant?.assistant_id) {
      let text = externalState?.brightbotText;
      manualSubmit(text);
    }
  }, [
    externalState?.brightbotText,
    activeAssistant?.assistant_id,
    // manualSubmit should NOT be here
  ]);

  useEffect(() => {
    if (threadId && thread !== threadId) {
      // clear steps and interrupt action
      clearSteps();
      setInterruptAction(null);
      // on going from one thread to another, clear the stream message and set stream animation chat to false
      setStream(false);
      setStreamMessage("");
      setLoading(true);
      setMessages([]);
      // if threadId comming from the url params is different from the current thread, set the new thread
      setThread(threadId);
      chatApi
        .get(`/threads/${threadId}/get_state`)
        .then((res) => {
          setMessages(res?.data?.values?.messages || []);
          fetchNewThread(
            threadId,
            res?.data?.metadata?.assistant_id,
            res?.data?.values
          );
        })
        .finally(() => setLoading(false));
    }
    // close sidebar if we going to create new session and clear the state
    if (!threadId) {
      // clear steps
      clearSteps();
      // clear interrupt action
      setInterruptAction(null);
      updateOutput([]);
      setSidebar(false);
      setStream(false);
    }
  }, [
    threadId,
    fetchNewThread,
    setLoading,
    setMessages,
    setSidebar,
    setStream,
    setStreamMessage,
    thread,
    setThread,
    updateOutput,
  ]);
};

export default useChatbotLifecycle;
